/**
 * Sidebar — entity-CRUD navigation.
 *
 * The sidebar is a flat list of NavLinks (Evals / Models). React
 * Router's NavLink handles isActive — there is
 * no shared layout state. One footer menu holds team and account controls.
 *
 * It folds to an icon rail (TRA-1085): with the ask panel open the review
 * page has four columns, and on a laptop the conversation is the column
 * that gives. Folded is remembered per browser, like the other reviewer
 * switches, and applies to every page because the nav is one element.
 *
 * Folding only hides labels. Every icon (logo mark, nav, fold toggle,
 * avatar) sits on one column centered 24px from the left edge and every row
 * keeps its height, so nothing moves or resizes. The folded rail is that
 * column: 48px plus the 1px border.
 */

import { useState } from 'react'
import { NavLink } from 'react-router-dom'
import { PanelLeftClose, PanelLeftOpen, Shield } from 'lucide-react'

import { NavEntryLink } from '@/components/layout/NavEntryLink'
import { TeamMenu } from '@/components/layout/TeamMenu'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import { useTeam } from '@/contexts/TeamContext'
import { API_BASE } from '@/lib/env'
import { isNavRouteVisible, NAV_ENTRIES } from '@/lib/nav'
import { readStoredFlag, writeStoredFlag } from '@/lib/storedFlag'

export const NAV_COLLAPSED_STORAGE_KEY = 'nav:collapsed'

/** Sidebar brand mark.
 *
 * Default: the TrainLoop horizontal lockup (icon + wordmark are ONE svg —
 * never recompose them from the icon plus text). Folded rail: the icon
 * file on its own, which is a complete mark too. Both files draw the mark
 * at the same viewBox coordinates, so rendering the icon at the lockup's
 * height keeps the mark's size and position when the rail folds.
 * Vanity org host (e.g. nolla.trainloop.ai): that customer's logo served
 * by the api from the data bucket (`org-logos/<slug>.svg`) — upload a
 * file there to brand a customer, no deploy needed. The SVG's own
 * width/height attributes control its rendered size. A customer mark is
 * a wordmark that cannot shrink to a rail, so the folded rail shows the
 * org's initial instead.
 * Missing object → serif org name, never a broken image or another
 * org's mark.
 */
function BrandMark({
  vanityOrg,
  collapsed,
}: {
  vanityOrg: string | null
  collapsed: boolean
}): React.JSX.Element {
  const [logoFailed, setLogoFailed] = useState(false)

  if (vanityOrg === null) {
    return collapsed ? (
      <img
        src="/Trainloop_Logo_Icon_Black.svg"
        alt="TrainLoop"
        className="h-9 w-auto object-contain"
      />
    ) : (
      <img
        src="/Trainloop_Logo_Horizontal_Black.svg"
        alt="TrainLoop"
        className="h-9 w-auto max-w-full object-contain object-left"
      />
    )
  }
  if (collapsed) {
    return (
      <span
        aria-label={vanityOrg}
        className="flex h-9 w-9 items-center justify-center font-serif text-lg font-semibold uppercase"
      >
        {vanityOrg.charAt(0)}
      </span>
    )
  }
  if (logoFailed) {
    return (
      <h1 className="font-serif text-lg font-semibold capitalize">{vanityOrg}</h1>
    )
  }
  return (
    <img
      src={`${API_BASE}/api/org-logos/${vanityOrg}.svg`}
      alt={vanityOrg}
      // Render at the SVG's own declared size (nolla: 100×15 — already
      // right); the caps only stop a future logo with huge intrinsic
      // dimensions from blowing up the sidebar.
      // Intrinsic-sized customer marks carry no internal whitespace (the
      // TrainLoop lockup does): ml-2.5 aligns their left edge with the
      // navigation icons.
      className="ml-2.5 max-h-9 max-w-full object-contain object-left"
      onError={() => { setLogoFailed(true) }}
    />
  )
}

export function Sidebar(): React.JSX.Element {
  const {
    isEmployee,
    isRestricted,
    hiddenNavItems,
    navConfigLoaded,
    vanityOrg,
  } = useTeam()
  const [collapsed, setCollapsed] = useState(() =>
    readStoredFlag(NAV_COLLAPSED_STORAGE_KEY, false),
  )

  function toggleCollapsed(): void {
    const next = !collapsed
    writeStoredFlag(NAV_COLLAPSED_STORAGE_KEY, next)
    setCollapsed(next)
  }

  const ToggleIcon = collapsed ? PanelLeftOpen : PanelLeftClose
  const toggleLabel = collapsed ? 'Expand navigation' : 'Collapse navigation'

  return (
    <aside
      data-collapsed={collapsed}
      className={`flex h-screen shrink-0 flex-col border-r border-border/60 bg-muted/35 backdrop-blur-xl ${
        collapsed ? 'w-[49px]' : 'w-48'
      }`}
    >
      {/* Logo. A fixed h-9 row, so any org's mark (or initial) starts the
          nav at the same height folded or not. px-1.5 puts the TrainLoop
          mark's center on the icon column. */}
      <div className="shrink-0 px-1.5 py-4">
        <NavLink to="/" className="flex h-9 items-center">
          <BrandMark vanityOrg={vanityOrg} collapsed={collapsed} />
        </NavLink>
      </div>

      {/* Primary nav */}
      <nav className="min-h-0 flex-1 space-y-1 overflow-y-auto px-2 py-2">
        {/* Per-team visibility (TRA-927): staff configure hidden routes on
            the Employees page; absent config shows everything. */}
        {NAV_ENTRIES.filter((entry) =>
          isNavRouteVisible({ to: entry.to, hiddenNavItems, navConfigLoaded })
        ).map((entry) => (
          <NavEntryLink key={entry.to} entry={entry} collapsed={collapsed} />
        ))}
        {/* Hidden on restricted demo teams so customer-facing demos stay clean (TRA-832) */}
        {isEmployee && !isRestricted && (
          <NavEntryLink
            entry={{ to: '/employees', label: 'TrainLoop', icon: Shield }}
            collapsed={collapsed}
          />
        )}
      </nav>

      {/* The fold control sits above the team menu, where the eye lands
          when the rail is folded and every other row is an icon. */}
      <div className="shrink-0 border-t border-border/60 px-2 py-2">
        <Tooltip>
          <TooltipTrigger asChild>
            <button
              type="button"
              aria-label={toggleLabel}
              aria-expanded={!collapsed}
              onClick={toggleCollapsed}
              className="flex min-h-8 w-full items-center gap-2 rounded-md px-2 py-1.5 text-xs text-muted-foreground hover:bg-foreground/5 hover:text-foreground focus-visible:outline focus-visible:outline-2 focus-visible:outline-ring"
            >
              <ToggleIcon aria-hidden="true" className="h-4 w-4 shrink-0" />
              {!collapsed && 'Collapse'}
            </button>
          </TooltipTrigger>
          <TooltipContent side="right" className="px-2 py-1 text-xs">
            {toggleLabel}
          </TooltipContent>
        </Tooltip>
      </div>

      <div className="shrink-0 border-t border-border/60 px-2 py-3">
        <TeamMenu collapsed={collapsed} />
      </div>
    </aside>
  )
}
